import React, { useState, useEffect, useRef, useContext, useMemo } from 'react';
import { TimeFieldType } from 'types/FieldTypes';
import { useTranslation } from 'react-i18next';
import FormFieldContext from '../FormFieldContext';
import { calculateDropdownPosition, useClickOutside } from 'services/utils/dropdownBehavior'
import { convertUTCToLocalTimeString, extractTimeFromLocalDateTime } from 'internationalization/timezoneConversions';
import { formatTime } from 'services/utils/timefieldFormatting';
import { formValidation } from 'services/utils/formValidation';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { useGlobalContext } from 'GlobalContext';
import { useSettings } from 'services/settings/SettingsContext';
import '../../../style/scss/live-edit.scss';
import '../../../style/scss/forms.scss';
import '../../../style/scss/tooltip.scss';

interface TimeInputProps extends TimeFieldType {
    id?: string;
    value?: string | null;
    isInvalid?: boolean;
    hideClearButton?: boolean;
    baseTime?: string | null;
    onChange?: (time: string | null) => void;
}

// The hour and minute parts of a time, hours always in 24 hour notation
interface TimeParts {
    hour: number;
    minute: number;
}

// The minute step of the picker per hours rounding setting of the job app settings
const MINUTE_STEPS: { [key: string]: number } = {
    'none': 5,
    '5min': 5,
    '15min': 15,
    '30min': 30,
    'hour': 30,
};

const TimeInput: React.FC<TimeInputProps> = ({
    name, id, value, timeRange, convertTimezone = false, utcDatetime, localDatetime, disabled, onChange, saveOnBlur, isInvalid,
    hideClearButton = false, baseTime
}) => {
    const { t } = useTranslation();
    const { errorMessages, setErrorMessages } = useGlobalContext();
    const { handleSubmit } = useContext(FormFieldContext);
    const { userLocale, userTimezone, getTimeFormat, environmentSettings } = useSettings();
    const [isOpen, setIsOpen] = useState(false);
    const [isCleared, setIsCleared] = useState(false);
    // Initialize the value directly to prevent an empty first paint of the field
    const [selectedTime, setSelectedTime] = useState(() => {
        if (convertTimezone && utcDatetime) {
            return convertUTCToLocalTimeString(utcDatetime, userLocale, userTimezone, getTimeFormat() === '12h');
        }
        if (localDatetime) {
            return extractTimeFromLocalDateTime(localDatetime, userLocale, getTimeFormat() === '12h');
        }
        if (value) {
            return value.slice(0, 5);
        }
        return '';
    });
    // The part of the time of which all options are shown as a grid instead of the ticker
    const [gridMode, setGridMode] = useState<'hours' | 'minutes' | null>(null);
    const is12HourFormat = (getTimeFormat() === '12h');
    const containerRef = useRef<HTMLDivElement>(null);
    const fieldRef = useRef<HTMLInputElement>(null);
    const [dropdownAbove, setDropdownAbove] = useState(false);
    const [,setDropdownMaxHeight] = useState<number | undefined>(undefined);

    // Get the translated period labels
    const amLabel = t('date_time.time.AM');
    const pmLabel = t('date_time.time.PM');

    // Determine the minute step from the hours rounding setting
    const minuteStep = MINUTE_STEPS[environmentSettings.mobile_round_off_hours || 'none'] || 5;

    // Set the current value if it exists
    useEffect(() => {
        // If convert timezone is true, convert the given utc datetime into the local timezone datetime and extract the time from it
        if (convertTimezone && utcDatetime) {
            const localTime = convertUTCToLocalTimeString(utcDatetime, userLocale, userTimezone, is12HourFormat);
            setSelectedTime(localTime);

        // If the datetime is already converted into local datetime, only extract the time from it
        } else if (localDatetime) {
            const timeString = extractTimeFromLocalDateTime(localDatetime, userLocale, is12HourFormat);
            setSelectedTime(timeString);

        // Otherwise just put in the given value
        } else if (!utcDatetime && !localDatetime && value) {

            // Remove seconds from time values if given: "17:00:00" -> "17:00"
            const formatted = value.slice(0,5)
            setSelectedTime(formatted);
        }
    }, [convertTimezone, utcDatetime, localDatetime, value]);

    // Parse a time string into its hour and minute parts, returns null when the string is no valid time
    const parseTimeParts = (time: string | null | undefined): TimeParts | null => {
        if (!time) return null;

        // Normalize the given time with the same formatting as the blur formatting
        const formatted = formatTime(time, is12HourFormat);
        const match = formatted.trim().match(/^(\d{1,2}):(\d{2})(?:\s*([A-Za-z]{2}))?$/);
        if (!match) return null;

        // Get the hour and minute values
        let hour = Number(match[1]);
        const minute = Number(match[2]);
        const period = match[3] ? match[3].toUpperCase() : null;

        // Convert a 12 hour value with a period to a 24 hour value
        if (period === pmLabel.toUpperCase() || period === 'PM') {
            hour = (hour % 12) + 12;
        } else if (period === amLabel.toUpperCase() || period === 'AM') {
            hour = hour % 12;
        }

        // Only accept valid hours and minutes
        if (hour < 0 || hour > 23 || minute < 0 || minute > 59) return null;

        return { hour, minute };
    };

    // Build the time string of the given parts in the format of the field
    const buildTimeString = (parts: TimeParts): string => {
        const paddedMinute = String(parts.minute).padStart(2, '0');

        // Show a 12 hour value with its period
        if (is12HourFormat) {
            const hour12 = parts.hour % 12 === 0 ? 12 : parts.hour % 12;
            const period = parts.hour < 12 ? amLabel : pmLabel;
            return `${hour12}:${paddedMinute} ${period}`;
        }

        return `${String(parts.hour).padStart(2, '0')}:${paddedMinute}`;
    };

    // Get the selectable range of the picker in minutes of the day
    const range = useMemo(() => {
        // Helper function to parse the given time range options
        const parseTime = (time: string) => {
            const [h, m] = time.split(':').map(Number)
            return h * 60 + m
        }

        // Use the full day when no range is given
        return {
            from: timeRange ? parseTime(timeRange.from) : 0,
            to: timeRange ? parseTime(timeRange.to) : 23 * 60 + 59,
        }
    }, [timeRange]);

    // Check whether the given parts fall within the selectable range
    const isWithinRange = (parts: TimeParts): boolean => {
        const totalMinutes = parts.hour * 60 + parts.minute;
        return totalMinutes >= range.from && totalMinutes <= range.to;
    };

    // Clamp the given parts to the selectable range
    const clampToRange = (parts: TimeParts): TimeParts => {
        const totalMinutes = Math.min(Math.max(parts.hour * 60 + parts.minute, range.from), range.to);
        return { hour: Math.floor(totalMinutes / 60), minute: totalMinutes % 60 };
    };

    // Determine the time the picker starts with for an empty field
    const getInitialParts = (): TimeParts => {
        // Start one hour after the given base time
        const baseParts = parseTimeParts(baseTime);
        if (baseParts) {
            return clampToRange({ hour: (baseParts.hour + 1) % 24, minute: baseParts.minute });
        }

        // Otherwise start at the current time rounded up to the minute step
        const now = new Date();
        let totalMinutes = Math.ceil((now.getHours() * 60 + now.getMinutes()) / minuteStep) * minuteStep;
        if (totalMinutes >= 24 * 60) totalMinutes = 24 * 60 - minuteStep;
        return clampToRange({ hour: Math.floor(totalMinutes / 60), minute: totalMinutes % 60 });
    };

    // The parts shown in the picker, the typed value when valid and otherwise the initial time
    const currentParts = useMemo<TimeParts>(() => {
        return parseTimeParts(selectedTime) || getInitialParts();
    }, [selectedTime, baseTime, is12HourFormat, range, minuteStep]);

    // Set the closest time in the field when it opens empty
    useEffect(() => {
        if (isOpen && !selectedTime && !isCleared) {
            handleSelect(buildTimeString(getInitialParts()));
        }
    }, [isOpen]);

    // Reset the grid mode when the picker closes
    useEffect(() => {
        if (!isOpen) setGridMode(null);
    }, [isOpen]);

    // When exiting the field, format the entered value and set the right selected time
    const onSearchBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
        let newTime = e.target.value;
        newTime = formatTime(e.target.value, is12HourFormat ?? false);
        setSelectedTime(newTime);

        // Validate the input of the field
        validateTime(newTime);

        setIsCleared(false);
        setTimeout(() => setIsOpen(false), 0);

        if (onChange) onChange(newTime);

        if (saveOnBlur && handleSubmit) {
            handleSubmit({ [name]: newTime });
        }
    };

    // Validate the entered time on blur
    const validateTime = (dataToValidate: any) => {
        const fieldItem = { name, type: 'time' };
        const error = formValidation(fieldItem, dataToValidate, is12HourFormat);

        if (error && error[name]) {
            const newErrorState = { ...errorMessages, [name]: error[name] };
            setErrorMessages(newErrorState);
            return false;
        } else {
            const newErrorState = { ...errorMessages, [name]: undefined };
            setErrorMessages(newErrorState);
            return true;
        }
    };

    // Handle the selection of a time
    const handleSelect = (newTime: string) => {
        if (onChange) onChange(newTime);
        setSelectedTime(newTime);
    }

    // Select the given parts when they fall within the range
    const selectParts = (parts: TimeParts) => {
        if (!isWithinRange(parts)) return;
        handleSelect(buildTimeString(parts));
    };

    // Step the hours of the current time, wrapping around the day when no range is set
    const stepHour = (delta: number) => {
        let hour = currentParts.hour + delta;
        if (!timeRange) hour = (hour + 24) % 24;
        selectParts({ hour, minute: currentParts.minute });
    };

    // Step the minutes of the current time to the next or previous step value
    const stepMinute = (delta: number) => {
        // Snap a value between the steps to the next step in the given direction
        let minute = delta > 0
            ? Math.floor(currentParts.minute / minuteStep) * minuteStep + minuteStep
            : Math.ceil(currentParts.minute / minuteStep) * minuteStep - minuteStep;

        // Wrap the minutes within the hour
        if (minute >= 60) minute = 0;
        if (minute < 0) minute = Math.floor(59 / minuteStep) * minuteStep;
        selectParts({ hour: currentParts.hour, minute });
    };

    // Toggle the period of the current time between am and pm
    const togglePeriod = () => {
        const hour = currentParts.hour < 12 ? currentParts.hour + 12 : currentParts.hour - 12;
        selectParts({ hour, minute: currentParts.minute });
    };

    // Build the selectable hours of the grid
    const hourOptions = useMemo(() => {
        // Show the hours of the current period in 12 hour format
        const hours = is12HourFormat
            ? Array.from({ length: 12 }, (_, i) => (currentParts.hour < 12 ? 0 : 12) + i)
            : Array.from({ length: 24 }, (_, i) => i);

        // Only keep hours within the selectable range
        return hours.filter(hour => hour * 60 + 59 >= range.from && hour * 60 <= range.to);
    }, [is12HourFormat, currentParts.hour, range]);

    // Build the selectable minutes of the grid for the current hour
    const minuteOptions = useMemo(() => {
        const minutes = Array.from({ length: Math.floor(60 / minuteStep) }, (_, i) => i * minuteStep);

        // Only keep minutes within the selectable range of the current hour
        return minutes.filter(minute => isWithinRange({ hour: currentParts.hour, minute }));
    }, [minuteStep, currentParts.hour, range]);

    // Handle the arrow keys in the field to step the minutes, or the hours with shift
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            const delta = e.key === 'ArrowUp' ? 1 : -1;
            if (!isOpen) openDropdown();
            if (e.shiftKey) {
                stepHour(delta);
            } else {
                stepMinute(delta);
            }
        } else if (e.key === 'Escape') {
            setIsOpen(false);
        }
    };

    // Get the error message from the errorState
    const errorMessage = errorMessages[name];

    // Use dropdownBehavior.ts to close the picker when clicking outside the field and the picker
    useClickOutside(isOpen, containerRef, setIsOpen);

    // Handle the opening of the picker
    const openDropdown = () => {
        if (!fieldRef.current) return;

        // Calculate the picker position
        calculateDropdownPosition(fieldRef, setDropdownAbove, setDropdownMaxHeight, 240);

        // Open the picker
        setIsOpen(true);
    };

    // Format the hour label of the ticker in the format of the field
    const hourLabel = is12HourFormat
        ? String(currentParts.hour % 12 === 0 ? 12 : currentParts.hour % 12)
        : String(currentParts.hour).padStart(2, '0');

    // Format the given hour option in the format of the field
    const formatHourOption = (hour: number) => is12HourFormat
        ? String(hour % 12 === 0 ? 12 : hour % 12)
        : String(hour).padStart(2, '0');

    return (
        <div className='custom-dropdown-container timefield-container' ref={containerRef}>
            <input
                type="text"
                name={name}
                id={id}
                ref={fieldRef}
                value={selectedTime}
                disabled={disabled}
                onChange={(e) => {
                    setSelectedTime(e.target.value);
                    openDropdown();
                    if (onChange) onChange(e.target.value);
                }}
                onBlur={onSearchBlur}
                onFocus={() => openDropdown()}
                onKeyDown={handleKeyDown}
                className={`${errorMessage || isInvalid ? 'is-invalid' : ''}`}
                placeholder={is12HourFormat ? t('date_time.time.12h_placeholder') : t('date_time.time.24h_placeholder')}
                autoComplete='off'
            />
            {selectedTime && !disabled && !hideClearButton && (
                <div className='clear-button tooltip-icon'
                     onClick={(e) => {
                     e.preventDefault();
                     setSelectedTime('');
                     setIsCleared(true);
                     fieldRef.current?.focus();}}>
                    <FontAwesomeIcon icon={faTimes} />
                    <span className="tooltip">{t('date_time.time.clear_time_label')}</span>
                </div>
            )}
            {isOpen && (
                // Prevent the mouse down inside the picker from blurring the field, so the picker stays open while ticking
                <div className={`custom-dropdown time-picker ${dropdownAbove ? 'dropdown-above' : ''}`}
                     onMouseDown={(e) => e.preventDefault()}>
                    {gridMode === null ? (
                        // Ticker with the hours, the minutes and in 12 hour format the period
                        <div className='time-picker-ticker'>
                            <div className='ticker-column'>
                                <button type='button' className='ticker-arrow' onClick={() => stepHour(1)} tabIndex={-1}>
                                    <FontAwesomeIcon icon={faChevronUp} />
                                </button>
                                <div className='ticker-value' onClick={() => setGridMode('hours')}>
                                    {hourLabel}
                                </div>
                                <button type='button' className='ticker-arrow' onClick={() => stepHour(-1)} tabIndex={-1}>
                                    <FontAwesomeIcon icon={faChevronDown} />
                                </button>
                            </div>
                            <div className='ticker-separator'>:</div>
                            <div className='ticker-column'>
                                <button type='button' className='ticker-arrow' onClick={() => stepMinute(1)} tabIndex={-1}>
                                    <FontAwesomeIcon icon={faChevronUp} />
                                </button>
                                <div className='ticker-value' onClick={() => setGridMode('minutes')}>
                                    {String(currentParts.minute).padStart(2, '0')}
                                </div>
                                <button type='button' className='ticker-arrow' onClick={() => stepMinute(-1)} tabIndex={-1}>
                                    <FontAwesomeIcon icon={faChevronDown} />
                                </button>
                            </div>
                            {is12HourFormat && (
                                <div className='ticker-column'>
                                    <button type='button' className='ticker-arrow' onClick={togglePeriod} tabIndex={-1}>
                                        <FontAwesomeIcon icon={faChevronUp} />
                                    </button>
                                    <div className='ticker-value period' onClick={togglePeriod}>
                                        {currentParts.hour < 12 ? amLabel : pmLabel}
                                    </div>
                                    <button type='button' className='ticker-arrow' onClick={togglePeriod} tabIndex={-1}>
                                        <FontAwesomeIcon icon={faChevronDown} />
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        // Grid with all selectable hours or minutes
                        <div className='time-picker-grid'>
                            {(gridMode === 'hours' ? hourOptions : minuteOptions).map(option => {
                                const isSelected = gridMode === 'hours' ? option === currentParts.hour : option === currentParts.minute;
                                return (
                                    <div key={option}
                                         className={`grid-item ${isSelected ? 'selected' : ''}`}
                                         onClick={() => {
                                             selectParts(gridMode === 'hours'
                                                 ? { hour: option, minute: currentParts.minute }
                                                 : { hour: currentParts.hour, minute: option });
                                             setGridMode(null);
                                         }}>
                                        {gridMode === 'hours' ? formatHourOption(option) : String(option).padStart(2, '0')}
                                    </div>
                                )
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default TimeInput;
